import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, ShoppingCart, Trash2 } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { fmt } from "@/lib/api";
import { Stars, StockPill } from "@/components/shop/ProductCard";

export default function WishlistPage() {
  const { wishlistProducts, wishlistLoading, toggleWishlist, moveToCart } = useStore();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen pb-24 pt-28" data-testid="wishlist-page">
      <div className="mx-auto max-w-7xl px-6 lg:px-12">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <p className="mb-3 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.25em] text-amber-500">
            <span className="h-px w-10 bg-amber-500" />
            Saved For Later
          </p>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-1 sm:text-4xl">Your Wishlist</h1>
          <p className="mt-2 text-sm text-3" data-testid="wishlist-count">
            {wishlistProducts.length} {wishlistProducts.length === 1 ? "product" : "products"}
          </p>
        </motion.div>

        {wishlistLoading ? (
          <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="wishlist-loading">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="animate-pulse overflow-hidden rounded-2xl border border-line bg-surface shadow-card">
                <div className="aspect-[4/3] bg-hover" />
                <div className="space-y-2.5 p-4">
                  <div className="h-4 w-2/3 rounded bg-hover" />
                  <div className="h-3 w-1/3 rounded bg-hover" />
                  <div className="h-10 w-full rounded-xl bg-hover" />
                </div>
              </div>
            ))}
          </div>
        ) : wishlistProducts.length === 0 ? (
          <div className="mt-12 flex flex-col items-center gap-4 rounded-2xl border border-line bg-surface py-24 text-center shadow-card" data-testid="wishlist-empty">
            <span className="flex h-20 w-20 items-center justify-center rounded-2xl border border-line bg-hover text-amber-500">
              <Heart className="h-9 w-9" strokeWidth={1.4} />
            </span>
            <h2 className="font-display text-2xl font-semibold tracking-tight text-1 sm:text-3xl">Nothing saved yet</h2>
            <p className="max-w-sm text-sm text-3">
              Tap the heart on any product to keep it here for later.
            </p>
            <button
              data-testid="wishlist-continue-shopping"
              onClick={() => navigate("/shop")}
              className="mt-3 rounded-full bg-amber-500 px-8 py-4 text-sm font-bold text-black transition-all hover:bg-amber-400"
            >
              Explore the Shop
            </button>
          </div>
        ) : (
          <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="wishlist-grid">
            {wishlistProducts.map((p, i) => (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(i * 0.05, 0.3), duration: 0.5 }}
                className="group flex flex-col overflow-hidden rounded-2xl border border-line bg-surface shadow-card transition-all duration-500 hover:-translate-y-1.5 hover:border-amber-500/25 hover:shadow-lift"
                data-testid={`wishlist-item-${p.id}`}
              >
                <div className="relative aspect-[4/3] cursor-pointer overflow-hidden bg-white p-4" onClick={() => navigate(`/shop/product/${p.id}`)}>
                  <img src={p.image} alt={p.name} loading="lazy" className="h-full w-full object-contain transition-transform duration-700 group-hover:scale-105" />
                  <button
                    data-testid={`wishlist-remove-${p.id}`}
                    onClick={(e) => { e.stopPropagation(); toggleWishlist(p.id); }}
                    className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full border border-strong bg-black/50 text-1 backdrop-blur transition-colors hover:border-red-400/60 hover:text-red-400"
                    aria-label="Remove from wishlist"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <div className="flex flex-1 flex-col p-4">
                  <h3 className="line-clamp-1 font-display text-base font-medium text-1">{p.name}</h3>
                  <div className="mt-1.5 flex items-center gap-1.5">
                    <Stars value={p.rating_avg} />
                    <span className="text-xs text-3">({p.rating_count})</span>
                  </div>
                  <div className="mt-2.5 flex items-center justify-between">
                    <span className="font-display text-lg font-semibold text-1">{fmt(p.price)}<span className="text-xs font-medium text-3">/{p.unit || "pc"}</span></span>
                    <StockPill status={p.status} />
                  </div>
                  <button
                    data-testid={`wishlist-move-to-cart-${p.id}`}
                    disabled={p.status === "out_of_stock"}
                    onClick={() => moveToCart(p)}
                    className={`mt-4 flex h-10 w-full items-center justify-center gap-2 rounded-xl text-sm font-bold transition-all ${
                      p.status === "out_of_stock"
                        ? "cursor-not-allowed border border-line text-3"
                        : "bg-amber-500 text-black hover:bg-amber-400"
                    }`}
                  >
                    <ShoppingCart className="h-4 w-4" />
                    {p.status === "out_of_stock" ? "Out of Stock" : "Move to Cart"}
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
